import React, { useState, useEffect } from 'react';
import { Plus, AlertTriangle, DollarSign, CheckCircle, XCircle, Edit, Trash2, Eye, Ban } from 'lucide-react';
import { Card, Button, Input, Select, Textarea, Table, Modal, Badge, ConfirmDialog, Loading } from '../components/ui';
import { sancionesService, departamentosService } from '../services/api';
import { formatMoney, formatDate, getLocalDate } from '../utils/formatters';
import { TIPOS_SANCION, ESTADOS_SANCION } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const SancionesPage = () => {
  const { hasPermission } = useAuth();
  const [sanciones, setSanciones] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ estado: '', tipo: '', anio: new Date().getFullYear() });
  
  const [form, setForm] = useState({
    departamento_id: '',
    tipo: 'RUIDO',
    descripcion: '',
    monto: '',
    fecha_infraccion: getLocalDate(),
    fecha_vencimiento: '',
    observaciones: ''
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sancionesRes, deptosRes, resumenRes] = await Promise.all([
        sancionesService.getAll(filters),
        departamentosService.getAll(),
        sancionesService.getResumen({ anio: filters.anio })
      ]);
      setSanciones(sancionesRes.data.data || []);
      setDepartamentos(deptosRes.data.data || []);
      setResumen(resumenRes.data.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected) {
        await sancionesService.update(selected.id, form);
        toast.success('Sanción actualizada');
      } else {
        await sancionesService.create(form);
        toast.success('Sanción registrada');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEdit = (sancion) => {
    setSelected(sancion);
    setForm({
      departamento_id: sancion.departamento_id,
      tipo: sancion.tipo,
      descripcion: sancion.descripcion,
      monto: sancion.monto,
      fecha_infraccion: sancion.fecha_infraccion?.split('T')[0] || sancion.fecha_infraccion,
      fecha_vencimiento: sancion.fecha_vencimiento?.split('T')[0] || '',
      observaciones: sancion.observaciones || ''
    });
    setModalOpen(true);
  };

  const handleMarcarPagada = async () => {
    try {
      await sancionesService.marcarPagada(selected.id, {});
      toast.success('Sanción marcada como pagada');
      setConfirmOpen(false);
      setSelected(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error');
    }
  };

  const handleAnular = async () => {
    try {
      await sancionesService.anular(selected.id, { motivo: 'Anulada por administración' });
      toast.success('Sanción anulada');
      setConfirmOpen(false);
      setSelected(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error');
    }
  };

  const handleDelete = async () => {
    try {
      await sancionesService.delete(selected.id);
      toast.success('Sanción eliminada');
      setConfirmOpen(false);
      setSelected(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openConfirm = (sancion, action) => {
    setSelected(sancion);
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const resetForm = () => {
    setSelected(null);
    setForm({
      departamento_id: '',
      tipo: 'RUIDO',
      descripcion: '',
      monto: '',
      fecha_infraccion: getLocalDate(),
      fecha_vencimiento: '',
      observaciones: ''
    });
  };

  const getEstadoVariant = (estado) => {
    const variants = { PENDIENTE: 'warning', PAGADA: 'success', APELADA: 'info', ANULADA: 'default' };
    return variants[estado] || 'default';
  };

  const getTipoColor = (tipo) => {
    const colors = {
      MOROSIDAD: 'danger',
      RUIDO: 'warning',
      MASCOTAS: 'info',
      AREAS_COMUNES: 'purple',
      ESTACIONAMIENTO: 'info',
      BASURA: 'warning',
      MODIFICACIONES: 'danger',
      CONDUCTA: 'danger',
      OTRO: 'default'
    };
    return colors[tipo] || 'default';
  };

  const columns = [
    { 
      header: 'Fecha', 
      render: (row) => formatDate(row.fecha_infraccion) 
    },
    { 
      header: 'Departamento', 
      render: (row) => (
        <div>
          <p className="font-medium text-white">Dpto {row.departamento_numero}</p>
          <p className="text-xs text-slate-500">{row.departamento_propietario}</p>
        </div>
      )
    },
    { 
      header: 'Tipo', 
      render: (row) => (
        <Badge variant={getTipoColor(row.tipo)}>
          {TIPOS_SANCION.find(t => t.value === row.tipo)?.label || row.tipo}
        </Badge>
      )
    },
    { 
      header: 'Descripción', 
      render: (row) => (
        <span className="text-slate-400 text-sm truncate max-w-xs block">{row.descripcion}</span>
      )
    },
    { 
      header: 'Monto', 
      render: (row) => (
        <span className="font-semibold text-amber-400">{formatMoney(row.monto)}</span>
      )
    },
    { 
      header: 'Estado', 
      render: (row) => (
        <Badge variant={getEstadoVariant(row.estado)}>
          {ESTADOS_SANCION.find(e => e.value === row.estado)?.label || row.estado}
        </Badge>
      )
    },
    { 
      header: 'Acciones', 
      render: (row) => (
        <div className="flex gap-1">
          <button 
            onClick={() => { setSelected(row); setModalDetalle(true); }}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"
            title="Ver detalle"
          >
            <Eye size={16} />
          </button>
          {row.estado === 'PENDIENTE' && hasPermission('ADMIN', 'PRESIDENTE', 'TESORERO') && (
            <>
              <button 
                onClick={() => openConfirm(row, 'pagar')}
                className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-emerald-400"
                title="Marcar como pagada"
              >
                <CheckCircle size={16} />
              </button>
              <button 
                onClick={() => handleEdit(row)}
                className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"
                title="Editar"
              >
                <Edit size={16} />
              </button>
              <button 
                onClick={() => openConfirm(row, 'anular')}
                className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-amber-400"
                title="Anular"
              >
                <Ban size={16} />
              </button>
            </>
          )}
          {hasPermission('ADMIN') && (
            <button 
              onClick={() => openConfirm(row, 'eliminar')}
              className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400"
              title="Eliminar"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  const totales = resumen?.totales || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Sanciones</h1>
          <p className="text-slate-400">Multas e infracciones a departamentos</p>
        </div>
        {hasPermission('ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO') && (
          <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>
            Nueva Sanción
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-500/20">
              <AlertTriangle className="text-amber-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Pendientes</p>
              <p className="text-2xl font-bold text-amber-400">{formatMoney(totales.monto_pendiente || 0)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-500/20">
              <CheckCircle className="text-emerald-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Cobradas</p>
              <p className="text-2xl font-bold text-emerald-400">{formatMoney(totales.monto_cobrado || 0)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/20">
              <DollarSign className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total {filters.anio}</p>
              <p className="text-2xl font-bold text-white">{formatMoney(totales.monto_total || 0)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-slate-700">
              <AlertTriangle className="text-slate-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Sanciones</p>
              <p className="text-2xl font-bold text-white">{totales.total_sanciones || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4">
          <Select
            label="Estado"
            value={filters.estado}
            onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
            options={ESTADOS_SANCION}
            placeholder="Todos"
            className="w-40"
          />
          <Select
            label="Tipo"
            value={filters.tipo}
            onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
            options={TIPOS_SANCION}
            placeholder="Todos"
            className="w-48"
          />
          <Select
            label="Año"
            value={filters.anio}
            onChange={(e) => setFilters({ ...filters, anio: e.target.value })}
            options={[
              { value: 2024, label: '2024' },
              { value: 2025, label: '2025' }
            ]}
            className="w-32"
          />
        </div>
      </Card>

      {/* Tabla */}
      <Card className="overflow-hidden">
        <Table columns={columns} data={sanciones} loading={loading} emptyMessage="No hay sanciones registradas" />
      </Card>

      {/* Modal Nueva/Editar */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Sanción' : 'Nueva Sanción'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Departamento"
              value={form.departamento_id}
              onChange={(e) => setForm({ ...form, departamento_id: e.target.value })}
              options={departamentos.map(d => ({ value: d.id, label: `${d.numero} - ${d.propietario}` }))}
              required
              disabled={!!selected}
            />
            <Select
              label="Tipo de Infracción"
              value={form.tipo}
              onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              options={TIPOS_SANCION}
              required
            />
          </div>
          <Textarea
            label="Descripción de la Infracción"
            value={form.descripcion}
            onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
            required
            rows={3}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Monto (S/)"
              type="number"
              step="0.01"
              value={form.monto}
              onChange={(e) => setForm({ ...form, monto: e.target.value })}
              required
            />
            <Input
              label="Fecha de Infracción"
              type="date"
              value={form.fecha_infraccion}
              onChange={(e) => setForm({ ...form, fecha_infraccion: e.target.value })}
              required
            />
            <Input
              label="Fecha de Vencimiento"
              type="date"
              value={form.fecha_vencimiento}
              onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })}
            />
          </div>
          <Textarea
            label="Observaciones"
            value={form.observaciones}
            onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
            rows={2}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">{selected ? 'Actualizar' : 'Registrar Sanción'}</Button>
          </div>
        </form>
      </Modal>

      {/* Modal Detalle */}
      <Modal isOpen={modalDetalle} onClose={() => setModalDetalle(false)} title="Detalle de Sanción">
        {selected && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-slate-700/50">
              <div className="flex items-center justify-between mb-3">
                <Badge variant={getEstadoVariant(selected.estado)} size="lg">
                  {selected.estado}
                </Badge>
                <span className="text-2xl font-bold text-amber-400">{formatMoney(selected.monto)}</span>
              </div>
              <p className="text-slate-400 text-sm">{selected.descripcion}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Departamento</p>
                <p className="text-white font-medium">Dpto {selected.departamento_numero}</p>
              </div>
              <div>
                <p className="text-slate-500">Propietario</p>
                <p className="text-white font-medium">{selected.departamento_propietario}</p>
              </div>
              <div>
                <p className="text-slate-500">Tipo</p>
                <Badge variant={getTipoColor(selected.tipo)} size="sm">
                  {TIPOS_SANCION.find(t => t.value === selected.tipo)?.label}
                </Badge>
              </div>
              <div>
                <p className="text-slate-500">Fecha Infracción</p>
                <p className="text-white">{formatDate(selected.fecha_infraccion)}</p>
              </div>
              {selected.fecha_vencimiento && (
                <div>
                  <p className="text-slate-500">Fecha Vencimiento</p>
                  <p className="text-white">{formatDate(selected.fecha_vencimiento)}</p>
                </div>
              )}
              {selected.fecha_pago && (
                <div>
                  <p className="text-slate-500">Fecha de Pago</p>
                  <p className="text-emerald-400">{formatDate(selected.fecha_pago)}</p>
                </div>
              )}
              <div>
                <p className="text-slate-500">Registrado por</p>
                <p className="text-white">{selected.registrado_por_nombre}</p>
              </div>
              {selected.aprobado_por_nombre && (
                <div>
                  <p className="text-slate-500">Aprobado por</p>
                  <p className="text-white">{selected.aprobado_por_nombre}</p>
                </div>
              )}
            </div>
            {selected.observaciones && (
              <div>
                <p className="text-slate-500 text-sm">Observaciones</p>
                <p className="text-slate-300 text-sm mt-1">{selected.observaciones}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (confirmAction === 'pagar') handleMarcarPagada();
          else if (confirmAction === 'anular') handleAnular();
          else if (confirmAction === 'eliminar') handleDelete();
        }}
        title={
          confirmAction === 'pagar' ? 'Marcar como Pagada' :
          confirmAction === 'anular' ? 'Anular Sanción' :
          'Eliminar Sanción'
        }
        message={
          confirmAction === 'pagar' ? `¿Confirmar que el departamento ${selected?.departamento_numero} pagó ${formatMoney(selected?.monto)}?` :
          confirmAction === 'anular' ? `¿Estás seguro de anular esta sanción de ${formatMoney(selected?.monto)}?` :
          `¿Eliminar permanentemente esta sanción?`
        }
        confirmText={
          confirmAction === 'pagar' ? 'Confirmar Pago' :
          confirmAction === 'anular' ? 'Anular' :
          'Eliminar'
        }
      />
    </div>
  );
};

export default SancionesPage;
